/**
 * health/models.ts — types for Module #9, the Roamer Health™.
 *
 * A platform MODULE, not a core engine. An organizational and informational
 * healthcare companion for roamers — health profile, local healthcare
 * directory, emergency services, travel health, medication support,
 * insurance prep, community knowledge, and predictive alerts.
 *
 * It does NOT diagnose conditions or replace licensed healthcare
 * professionals — every informational surface carries HEALTH_DISCLAIMER.
 * Health data is HIGHLY sensitive: private by default, granular consent
 * toggles, nothing shared without explicit opt-in. Honesty rules apply:
 * unknown ⇒ null, directory entries / readings / reports exist only as
 * recorded data (future providers sync real data — nothing fabricated).
 */

export const HEALTH_DISCLAIMER =
  'Informational only — not medical advice. Always consult a licensed healthcare professional.';

// ─── Health profile (user-managed; users choose what to store) ─────────────────

export interface EmergencyContact {
  id: string;
  name: string;
  relationship: string | null;
  phone: string;
  addedAt: number;
}

export interface HealthProfile {
  bloodType: string | null; // optional — user's choice
  allergies: string[];
  preferredLanguage: string | null;
  travelInsuranceProvider: string | null;
  insurancePolicyNumber: string | null;
  primaryPhysician: string | null;
  medicalNotes: string | null;
}

export const DEFAULT_HEALTH_PROFILE: HealthProfile = {
  bloodType: null,
  allergies: [],
  preferredLanguage: null,
  travelInsuranceProvider: null,
  insurancePolicyNumber: null,
  primaryPhysician: null,
  medicalNotes: null,
};

// ─── Local healthcare directory (recorded entries only) ────────────────────────

export const PROVIDER_KINDS = [
  'hospital',
  'clinic',
  'urgentCare',
  'dentist',
  'pharmacy',
  'pharmacy24h',
  'telehealth',
  'emergencyRoom',
  'specialist',
] as const;
export type ProviderKind = (typeof PROVIDER_KINDS)[number];

export interface HealthcareProvider {
  id: string;
  kind: ProviderKind;
  name: string;
  city: string;
  country: string | null;
  address: string | null;
  phone: string | null;
  englishSpeaking: boolean | null; // null = unknown, never assumed
  specialty: string | null; // for kind 'specialist'
  notes: string | null;
  addedAt: number;
}

// ─── Emergency services (recorded per-country directories) ─────────────────────

export interface EmergencyDirectory {
  country: string;
  /** All numbers nullable — an unavailable service stays honestly null. */
  general: string | null;
  police: string | null;
  fire: string | null;
  ambulance: string | null;
  touristPolice: string | null;
  poisonControl: string | null;
  embassies: { name: string; phone: string | null; address: string | null }[];
  consulates: { name: string; phone: string | null; address: string | null }[];
  /** Emergency translation phrases: { phrase, translation, language }. */
  phrases: { phrase: string; translation: string; language: string }[];
  recordedAt: number;
}

// ─── Travel health (recorded location-aware readings) ──────────────────────────

export const HEALTH_METRICS = [
  'airQuality',
  'uvIndex',
  'heatIndex',
  'pollenLevel', // future feeds
  'mosquitoAdvisory',
  'waterSafety',
  'seasonalNotice',
  'vaccinationGuidance', // informational only
] as const;
export type HealthMetric = (typeof HEALTH_METRICS)[number];

export interface TravelHealthReading {
  id: string;
  metric: HealthMetric;
  city: string;
  /** Numeric value where sensible (AQI, UV, heat °C) — else null. */
  value: number | null;
  /** Textual advisory (e.g. "tap water not potable") — else null. */
  note: string | null;
  /** Named source is REQUIRED — no unsourced health information. */
  source: string;
  recordedAt: number;
}

// ─── Medication support ────────────────────────────────────────────────────────

export interface Medication {
  id: string;
  name: string;
  dosage: string | null;
  /** Refill reminder date (ms) — null when the user doesn't want one. */
  refillDueAt: number | null;
  /** Recorded translations: { language, translatedName }. */
  translations: { language: string; translatedName: string }[];
  /** Recorded equivalent names abroad, with country — where available only. */
  equivalents: { country: string; equivalentName: string; source: string }[];
  notes: string | null;
  addedAt: number;
}

// ─── Insurance (FUTURE — interfaces only, no provider integration) ─────────────

export interface InsurancePolicyRef {
  id: string;
  kind: 'travel' | 'health';
  provider: string;
  policyNumber: string | null;
  /** Expiry (ms) drives the insurance-expiration alert. */
  expiresAt: number | null;
  emergencyAssistanceNumber: string | null;
  notes: string | null;
  addedAt: number;
}

/** Future claim-tracking reference — no provider is ever called. */
export interface InsuranceClaimRef {
  id: string;
  policyId: string;
  description: string;
  status: 'draft' | 'submitted' | 'approved' | 'denied' | 'paid';
  openedAt: number;
}

/** Registry hook for FUTURE insurance provider integrations. Unimplemented. */
export interface InsuranceProviderConnector {
  id: string;
  name: string;
}

// ─── Community knowledge (Roamer Reports™ — separated from official info) ─────

export const REPORT_KINDS = [
  'englishSpeakingDoctor',
  'affordableDentalClinic',
  'reliablePharmacy',
  'hospitalWaitTimes',
  'healthcareExperience',
] as const;
export type HealthReportKind = (typeof REPORT_KINDS)[number];

export interface RoamerHealthReport {
  id: string;
  kind: HealthReportKind;
  city: string;
  text: string;
  /** Community experience vs. official information — never mixed. */
  origin: 'community' | 'official';
  verified: boolean;
  /** Named source required for official entries. */
  source: string | null;
  recordedAt: number;
}

// ─── Predictive alerts (rule-based over recorded data; informational) ──────────

export const HEALTH_ALERT_KINDS = [
  'extremeHeat',
  'poorAirQuality',
  'medicationRefill',
  'insuranceExpiration',
  'vaccinationInfo',
  'weatherHealthAdvisory',
] as const;
export type HealthAlertKind = (typeof HEALTH_ALERT_KINDS)[number];

export interface HealthAlert {
  /** Deterministic id — same conditions always produce the same id. */
  id: string;
  kind: HealthAlertKind;
  message: string;
  /** Recorded evidence backing the alert. Never empty. */
  basis: string[];
  disclaimer: typeof HEALTH_DISCLAIMER;
}

// ─── Privacy (health data: private by default, granular consent) ───────────────

export interface HealthPrivacySettings {
  /** Master consent — off ⇒ export returns nothing sensitive, no publications. */
  sharingConsent: boolean;
  shareProfile: boolean;
  shareEmergencyContacts: boolean;
  shareMedications: boolean;
  shareInsurance: boolean;
  shareReports: boolean;
  /** Gates bus notifications for alerts (payload carries NO health details). */
  allowAlertNotifications: boolean;
}

export const DEFAULT_HEALTH_PRIVACY: HealthPrivacySettings = {
  sharingConsent: false,
  shareProfile: false,
  shareEmergencyContacts: false,
  shareMedications: false,
  shareInsurance: false,
  shareReports: false,
  allowAlertNotifications: false,
};

/**
 * FUTURE encryption hook. Storage is plaintext localStorage until a real
 * provider is registered — the module never pretends data is encrypted.
 */
export interface HealthEncryptionProvider {
  id: string;
  encrypt(plaintext: string): string;
  decrypt(ciphertext: string): string;
}

// ─── Export / backup ───────────────────────────────────────────────────────────

export interface HealthExport {
  exportedAt: number;
  disclaimer: typeof HEALTH_DISCLAIMER;
  profile: HealthProfile | null;
  emergencyContacts: EmergencyContact[];
  medications: Medication[];
  policies: InsurancePolicyRef[];
  claims: InsuranceClaimRef[];
  reports: RoamerHealthReport[];
  providers: HealthcareProvider[];
  directories: EmergencyDirectory[];
  readings: TravelHealthReading[];
}
